import React, { useEffect } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Accordion, AccordionDetails, AccordionSummary, makeStyles, useLoading, useUseCase } from '@smile-next/uikit';
import { LoginForm } from '@/components/LoginForm/LoginForm';
import { LoginFormBody } from '@/components/LoginForm/LoginFormBody';
import { LoginFormFooter } from '@/components/LoginForm/LoginFormFooter';
import { LoginFormHeader } from '@/components/LoginForm/LoginFormHeader';
import { SelectLoginMethodPageState } from '@/containers/SelectLoginMethod/Page/SelectLoginMethodPageState';
import { SelectLoginMethodForm } from '@/containers/SelectLoginMethod/Page/components/SelectLoginMethodForm';
import { useLocationSearch } from '@/hooks/useLocationSearch';
import { useRouter } from '@/hooks/useRouter';
import { DisplayErrorMessageUseCase } from '@/usecases/DisplayErrorMessageUseCase';
import { DisplayLambdaErrorMessageUseCase } from '@/usecases/DisplayLambdaErrorMessageUseCase';
import { LoadAuthParamsUseCase } from '@/usecases/LoadAuthParamsUseCase';
import { VerifyAuthenticatedUserUseCase } from '@/usecases/VerifyAuthenticatedUserUseCase';
import { removeHubListener } from '@/utils/hubManagement';

const useStyles = makeStyles((theme) => ({
    root: {
        height: 'calc(100vh - 56px)',
        display: 'flex',
        flexDirection: 'column',
    },
    main: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        flex: '1 1 auto',
    },
    errorMessage: {
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.error.main,
        margin: theme.spacing(1, 0),
    },
}));

export const SelectLoginMethodPage: React.FC<RouteComponentProps> = () => {
    const { state, usecase } = useUseCase(new SelectLoginMethodPageState());
    const { cloudId, loginMethod, select, error, lambdaError, errorDescription } = useLocationSearch();
    const {
        movePageAfterSuccessfulLogin,
        navigateToLoginPage,
        navigateToNextAccountLoginPage,
        navigateToSSOLoginPage,
    } = useRouter();
    const { startCoverLoading, endLoadingByName } = useLoading();
    const classes = useStyles();

    useEffect(() => {
        // SSOコールバックページでつけたリスナーを削除する
        removeHubListener();
    }, []);

    useEffect(() => {
        startCoverLoading('SelectLoginMethodVerifyAuthenticatedUser');
    }, [startCoverLoading]);

    useEffect(() => {
        if (state.isAuthenticated === undefined) {
            // 初回レンダー時は無視する
        } else if (state.isAuthenticated) {
            movePageAfterSuccessfulLogin();
        } else {
            navigateToLoginPage(state.authParams);
        }
    }, [movePageAfterSuccessfulLogin, navigateToLoginPage, state.authParams, state.isAuthenticated]);

    useEffect(() => {
        usecase(new LoadAuthParamsUseCase()).execute({
            cloudId,
            loginMethod,
        });

        if (error) {
            // error発生時は自動ログインできないため、自動ログイン処理を無視し、エラーメッセージ表示のためのUseCaseを実行する。
            endLoadingByName('SelectLoginMethodVerifyAuthenticatedUser');
            usecase(new DisplayErrorMessageUseCase()).execute(error, errorDescription);
            return;
        }

        if (lambdaError) {
            // Lambdaでのエラー発生時は自動ログインできないため、自動ログイン処理を無視し、エラーメッセージ表示のためのUseCaseを実行する。
            endLoadingByName('SelectLoginMethodVerifyAuthenticatedUser');
            usecase(new DisplayLambdaErrorMessageUseCase()).execute(lambdaError);
            return;
        }

        if (select) {
            // ログイン方法を明示的に選択したいため、自動ログイン処理を無視する。
            endLoadingByName('SelectLoginMethodVerifyAuthenticatedUser');
            return;
        }

        // 現在の認証情報を検証する
        usecase(new VerifyAuthenticatedUserUseCase())
            .execute()
            .catch(() => {
                // エラー発生時の処理は別途行っているためここでは何も行わない
            })
            .finally(() => {
                endLoadingByName('SelectLoginMethodVerifyAuthenticatedUser');
            });
    }, [cloudId, endLoadingByName, error, errorDescription, lambdaError, loginMethod, select, usecase]);

    return (
        <div className={classes.root}>
            <div className={classes.main}>
                <LoginForm>
                    <LoginFormHeader />
                    <LoginFormBody>
                        <SelectLoginMethodForm
                            authParams={state.authParams}
                            onClickNextIdLoginButton={navigateToNextAccountLoginPage}
                            onClickSSOButton={navigateToSSOLoginPage}
                        />
                        {state.errorMessage && <div className={classes.errorMessage}>{state.errorMessage}</div>}
                        {state.additionalMessageDetails && (
                            <Accordion id="additionalMessage">
                                <AccordionSummary>{state.additionalMessageSummary ?? ''}</AccordionSummary>
                                <AccordionDetails>{state.additionalMessageDetails}</AccordionDetails>
                            </Accordion>
                        )}
                    </LoginFormBody>
                    <LoginFormFooter />
                </LoginForm>
            </div>
        </div>
    );
};
